<template>
  <div class="account-page">
    <section class="account-content">
      <div class="container">
        <div class="auth-container" v-if="!isLoggingOutFromPassword">
          <!-- User Profile (Logged In) -->
          <div v-if="isLoggedIn" class="user-profile-layout">
            <div class="row gx-lg-5">
              <!-- Sidebar Menu -->
              <div class="col-lg-4 mb-5 mb-lg-0">
                <div class="account-sidebar">
                  <div class="sidebar-header mb-4 d-none d-lg-block border-bottom pb-3">
                    <h2 class="h5 fw-bold mb-1">{{ currentUser.name }}</h2>
                    <p class="small text-muted mb-0">{{ currentUser.email }}</p>
                  </div>
                  
                  <nav class="nav flex-column account-nav">
                    <button 
                      v-for="tab in tabs" 
                      :key="tab.id"
                      @click="!tab.disabled && (activeTab = tab.id)"
                      class="nav-link text-start"
                      :class="{ 
                        'active': activeTab === tab.id,
                        'disabled': tab.disabled
                      }"
                      :disabled="tab.disabled"
                    >
                      {{ t(tab.label) }}
                    </button>
                    
                    <button @click="handleLogout" :disabled="isLoggingOut" class="nav-link text-start text-danger logout-link mt-2">
                      <span v-if="isLoggingOut" class="spinner-border spinner-border-sm me-2"></span>
                      {{ t('header.user.logout').toUpperCase() }}
                    </button>
                  </nav>
                </div>
              </div>

              <!-- Main Content Area -->
              <div class="col-lg-8">
                <div class="account-main-content">
                  
                  <!-- Pending Payments Alert -->
                  <div v-if="pendingOrdersCount > 0 && activeTab !== 'orders' && !configStore.isCountdownMode" class="pending-orders-alert mb-4 p-3 d-flex align-items-center justify-content-between">
                    <div class="d-flex align-items-center gap-3">
                      <div class="alert-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                      </div>
                      <div>
                        <p class="mb-0 fw-bold small text-uppercase letter-spacing-1">Hai {{ pendingOrdersCount }} ordine/i in attesa di pagamento</p>
                        <p class="mb-0 small text-muted">Completa il pagamento per confermare il tuo acquisto.</p>
                      </div>
                    </div>
                    <button @click="activeTab = 'orders'" class="btn btn-dark btn-sm text-uppercase letter-spacing-1 fw-bold">Vedi Ordini</button>
                  </div>

                  <!-- Section: Personal Data -->
                  <div v-if="activeTab === 'personal-data'" class="tab-content-section">
                    <div v-if="!isEditMode" class="mb-4 text-center">
                      <router-link 
                        :to="configStore.isCountdownMode ? '/countdown' : '/'" 
                        class="btn btn-premium-white d-inline-flex align-items-center justify-content-center gap-2 py-2 px-4 text-uppercase fw-bold letter-spacing-1"
                        style="font-size: 10px;"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
                        </svg>
                        {{ t('account.personal_data.btn.back_to_home') }}
                      </router-link>
                    </div>

<div class="d-flex justify-content-between align-items-end mb-4 pb-2 border-bottom-dark">
                      <div>
                        <h2 class="h6 text-uppercase fw-bold mb-0 letter-spacing-2" style="font-size: 12px;">{{ t('account.personal_data.title') }}</h2>
                        <p class="small text-muted mb-0 mt-1" style="font-size: 9px; letter-spacing: 0.3px;">{{ t('account.personal_data.subtitle') }}</p>
                      </div>
                    </div>

                    <div class="profile-card">
                      <div class="card-body">
                        <!-- View Mode -->
                        <div v-if="!isEditMode" class="info-list">
                          <!-- Prima riga: Nome e Cognome -->
                          <div class="field-row">
                            <div class="field-group">
                              <span class="field-label">{{ t('account.personal_data.label.name').toUpperCase() }}</span>
                              <span class="field-value">{{ authStore.user?.firstName || '-' }}</span>
                            </div>
                            <div class="field-group">
                              <span class="field-label">{{ t('account.personal_data.label.surname').toUpperCase() }}</span>
                              <span class="field-value">{{ authStore.user?.lastName || '-' }}</span>
                            </div>
                          </div>

                          <!-- Seconda riga: Email e Telefono -->
                          <div class="field-row">
                            <div class="field-group">
                              <span class="field-label">{{ t('account.personal_data.label.email').toUpperCase() }}</span>
                              <span class="field-value">{{ authStore.user?.email || '-' }}</span>
                            </div>
                            <div class="field-group">
                              <span class="field-label">{{ t('account.personal_data.label.phone').toUpperCase() }}</span>
                              <span class="field-value">{{ authStore.user?.phone || '-' }}</span>
                            </div>
                          </div>

                          <!-- Terza riga: Member Since e Consenso Marketing -->
                          <div class="field-row">
                            <div class="field-group">
                              <span class="field-label">{{ t('account.personal_data.member_since').toUpperCase() }}</span>
                              <span class="field-value text-teal">{{ formatDate(getRegistrationDate()) }}</span>
                            </div>
                            <div class="field-group">
                              <span class="field-label">{{ t('account.personal_data.marketing_consent').toUpperCase() }}</span>
                              <span class="field-value">
                                <span class="badge" :class="authStore.user?.isMarketingAllowed ? 'badge-positive' : 'badge-neutral'">
                                  {{ authStore.user?.isMarketingAllowed ? t('account.personal_data.marketing_accepted') : t('account.personal_data.marketing_declined') }}
                                </span>
                              </span>
                            </div>
                          </div>

                          <!-- Quarta riga: Privacy (Full Width) -->
                          <div class="field-row">
                            <div class="field-group full">
                              <span class="field-label">{{ t('auth.register.gdpr.privacy_link').toUpperCase() }}</span>
                              <span class="field-value">
                                <span class="badge" :class="authStore.user?.isPrivacyAccepted ? 'badge-positive' : 'badge-neutral'">
                                  {{ authStore.user?.isPrivacyAccepted ? t('account.personal_data.privacy_accepted') : t('account.personal_data.privacy_declined') }}
                                </span>
                              </span>
                            </div>
                          </div>
                        </div>

                        <!-- Edit Mode -->
                        <div v-else class="edit-form-wrap">
                          <form @submit.prevent="handleSaveProfile" class="premium-form">
                            <div class="row gx-4">
                              <div class="col-md-6">
                                <div class="form-group mb-3">
                                  <label for="editFirstName" class="form-label-premium">{{ t('account.personal_data.label.name') }}</label>
                                  <input id="editFirstName" name="firstName" v-model.trim="editForm.firstName" type="text" class="form-control" required>
                                </div>
                              </div>
                              <div class="col-md-6">
                                <div class="form-group mb-3">
                                  <label for="editLastName" class="form-label-premium">{{ t('account.personal_data.label.surname') }}</label>
                                  <input id="editLastName" name="lastName" v-model.trim="editForm.lastName" type="text" class="form-control" required>
                                </div>
                              </div>
                            </div>

                            <div class="row gx-4">
                              <div class="col-md-6">
                                <div class="form-group mb-3">
                                  <label for="editEmail" class="form-label-premium">{{ t('account.personal_data.label.email_not_editable') }}</label>
                                  <input id="editEmail" name="email" type="email" class="form-control bg-light" :value="authStore.user?.email" disabled>
                                </div>
                              </div>
                              <div class="col-md-6">
                                <div class="form-group mb-3">
                                  <label for="editPhone" class="form-label-premium">{{ t('account.personal_data.label.phone') }}</label>
                                  <input id="editPhone" name="phone" v-model.trim="editForm.phone" type="tel" class="form-control">
                                </div>
                              </div>
                            </div>

                            <div class="form-group mb-4">
                              <div class="form-check custom-checkbox-premium">
                                <input 
                                  class="form-check-input" 
                                  type="checkbox" 
                                  id="marketingConsent" 
                                  v-model="editForm.isMarketingAllowed"
                                >
                                <label class="form-check-label small fw-bold text-uppercase letter-spacing-1" for="marketingConsent" style="font-size: 9px;">
                                  {{ t('auth.register.gdpr.marketing_accept') }}
                                </label>
                              </div>
                            </div>

                            <div class="d-flex gap-2 justify-content-end">
                              <button type="button" class="btn btn-outline-secondary btn-sm px-3" @click="cancelEdit" :disabled="authStore.isLoading" style="font-size: 9px;">{{ t('account.personal_data.btn.cancel') }}</button>
                              <button type="submit" class="btn btn-dark btn-sm px-4" :disabled="authStore.isLoading" style="font-size: 9px;">
                                {{ authStore.isLoading ? t('account.personal_data.btn.saving') : t('account.personal_data.btn.save') }}
                              </button>
                            </div>
                          </form>
                        </div>
                      </div>

                      <!-- Card footer con azioni -->
                      <div class="card-footer" v-if="!isEditMode">
                        <div class="footer-actions d-flex justify-content-between align-items-center gap-2">
                          <div>
                            <button @click="openChangePasswordModal" class="btn btn-ghost-dark py-1 px-3" style="font-size: 9px;">{{ t('account.personal_data.btn.change_password') }}</button>
                          </div>
                          <div>
                            <button @click="startEdit" class="btn btn-dark btn-sm px-4" style="font-size: 9px;">{{ t('account.personal_data.btn.edit') }}</button>
                          </div>
                        </div>
                      </div>

                      <!-- Privacy & Cookies Compact -->
                      <div class="pt-6 mt-3 border-top-dark">
                        <h3 class="h6 fw-bold text-uppercase mb-3 letter-spacing-1" style="font-size: 10px; padding-top: 12px; margin-top: 0;">{{ t('account.personal_data.privacy_title') }}</h3>
                        <div class="bg-light p-3 mt-2">
                          <div class="privacy-consent-box d-flex justify-content-between align-items-center mb-0 gap-3">
                            <div class="consent-info-text">
                              <span class="info-label d-block mb-1" style="flex: none;">{{ t('account.personal_data.label.consent_id').toUpperCase() }}</span>
                              <span class="info-value small font-monospace text-muted text-break" style="font-size: 9px; letter-spacing: 0.5px;">{{ cookieStore.consentId }}</span>
                            </div>
                            <button @click="cookieStore.openSettings()" class="btn btn-dark btn-sm px-4 py-2 flex-shrink-0" style="font-size: 9px;">
                              {{ t('cookies.banner.btn_manage') }}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Section: Address Manager -->
                  <div v-if="activeTab === 'addresses'" class="tab-content-section">
                    <AddressManager />
                  </div>

                  <!-- Section: Orders -->
                  <div v-if="activeTab === 'orders'" class="tab-content-section">
                    <h2 class="h4 text-uppercase fw-bold mb-5 letter-spacing-1">{{ t('account.orders.title') }}</h2>
                    <OrderHistory />
                  </div>

                </div>
              </div>
            </div>
          </div>

          <!-- Change Password Modal -->
          <div v-if="showChangePasswordModal" class="modal-overlay" @click.self="closeChangePasswordModal">
            <div class="modal-content border-0 shadow-lg">
              <div class="modal-header border-0 pb-0">
                <h3 class="modal-title h6 fw-bold text-uppercase letter-spacing-2">{{ t('account.password_modal.title') }}</h3>
                <button @click="closeChangePasswordModal" class="modal-close">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>

              <form @submit.prevent="handleChangePassword" class="modal-form p-4">
                <div class="form-group mb-3">
                  <label class="form-label-premium" for="currentPassword">{{ t('account.password_modal.current_pass') }}</label>
                  <div class="password-wrapper">
                    <input 
                      id="currentPassword"
                      name="currentPassword"
                      v-model.trim="changePasswordForm.currentPassword" 
                      :type="showCurrentPassword ? 'text' : 'password'" 
                      class="form-control"
                      required
                    >
                    <button type="button" class="password-toggle" @click="showCurrentPassword = !showCurrentPassword">
                      <svg v-if="!showCurrentPassword" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                      <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                    </button>
                  </div>
                </div>

                <div class="form-group mb-3">
                  <label class="form-label-premium" for="newPassword">{{ t('account.password_modal.new_pass') }}</label>
                  <div class="password-wrapper">
                    <input 
                      id="newPassword"
                      name="newPassword"
                      v-model.trim="changePasswordForm.newPassword" 
                      :type="showNewPassword ? 'text' : 'password'" 
                      class="form-control"
                      required
                    >
                    <button type="button" class="password-toggle" @click="showNewPassword = !showNewPassword">
                      <svg v-if="!showNewPassword" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                      <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                    </button>
                  </div>
                  <div class="password-requirements mt-3">
                    <ul class="requirements-list list-unstyled small">
                      <li v-for="(val, key) in passwordValidation" :key="key" :class="{ 'text-teal': val, 'text-muted': !val }" class="mb-1 d-flex align-items-center">
                        <span class="me-2">{{ val ? '✓' : '•' }}</span> {{ t(`account.password_modal.req_${key === 'minLength' ? 'min_chars' : key}`) }}
                      </li>
                    </ul>
                  </div>
                </div>

                <div class="form-group mb-4">
                  <label class="form-label-premium" for="confirmPassword">{{ t('account.password_modal.confirm_pass') }}</label>
                  <div class="password-wrapper">
                    <input 
                      id="confirmPassword"
                      name="confirmPassword"
                      v-model.trim="changePasswordForm.confirmPassword" 
                      :type="showConfirmPassword ? 'text' : 'password'" 
                      class="form-control"
                      required
                    >
                    <button type="button" class="password-toggle" @click="showConfirmPassword = !showConfirmPassword">
                      <svg v-if="!showConfirmPassword" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                      <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                    </button>
                  </div>
                </div>

                <div v-if="changePasswordError" class="alert alert-danger rounded-0 small py-2 mb-4">
                  {{ changePasswordError }}
                </div>

                <div class="modal-actions d-flex flex-column gap-2">
                  <button type="submit" class="btn btn-dark w-100 py-3" :disabled="authStore.isLoading || !isPasswordValid">
                    {{ authStore.isLoading ? t('account.personal_data.btn.saving') : t('account.personal_data.btn.save') }}
                  </button>
                  <button type="button" class="btn btn-link text-muted text-decoration-none small text-uppercase fw-bold letter-spacing-1" @click="closeChangePasswordModal">
                    {{ t('account.personal_data.btn.cancel') }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/authStore'
import { useConfigStore } from '@/stores/configStore'
import { useCookieStore } from '@/stores/useCookieStore'
import { useToast } from '@/composables/useToast'
import { useLanguage } from '@/composables/useLanguage'
import AddressManager from '@/components/AddressManager.vue'
import OrderHistory from '@/components/OrderHistory.vue'

import { salesOrderService } from '@/services/salesOrderService'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const configStore = useConfigStore()
const cookieStore = useCookieStore()
const toast = useToast()
const { t } = useLanguage()

const activeTab = ref(route.query.tab || 'personal-data')
const pendingOrdersCount = ref(0)

const fetchPendingOrdersCount = async () => {
  try {
    const response = await salesOrderService.getMyOrders()
    const data = response.items || response.data || (Array.isArray(response) ? response : [])
    if (Array.isArray(data)) {
      pendingOrdersCount.value = data.filter(o => {
        const status = String(o.orderStatus ?? o.status ?? o.Status ?? '').toLowerCase()
        return ['pendingpayment', 'pending'].includes(status)
      }).length
    }
  } catch (e) {
    // Fallback silenzioso
  }
}

// Watch for query changes to switch tabs
watch(() => route.query.tab, (newTab) => {
  if (newTab) {
    activeTab.value = newTab
  }
})

// Watch for tab changes to load profile data when entering personal-data tab
watch(activeTab, (newTab) => {
  if (newTab === 'personal-data') {
    loadProfileData()
  }
})

const loadProfileData = async () => {
  try {
    await authStore.getProfile()
  } catch (err) {
    console.error('Failed to load profile:', err)
  }
}

const isEditMode = ref(false)

onMounted(() => {
  if (!configStore.isCountdownMode) {
    fetchPendingOrdersCount()
  }
  loadProfileData()
})
const showChangePasswordModal = ref(false)
const isLoggingOut = ref(false)
const isLoggingOutFromPassword = ref(false)
const changePasswordError = ref('')

const tabs = computed(() => {
  return [
    { id: 'personal-data', label: 'account.menu.personal_data', disabled: false },
    { id: 'addresses', label: 'account.menu.addresses', disabled: configStore.isCountdownMode },
    { id: 'orders', label: 'account.menu.orders', disabled: configStore.isCountdownMode }
  ]
})

const editForm = ref({
  firstName: '',
  lastName: '',
  phone: '',
  isMarketingAllowed: false
})

const changePasswordForm = ref({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
})

const showCurrentPassword = ref(false)
const showNewPassword = ref(false)
const showConfirmPassword = ref(false)

const isLoggedIn = computed(() => authStore.isAuthenticated)
const currentUser = computed(() => ({
  name: `${authStore.user?.firstName || ''} ${authStore.user?.lastName || ''}`.trim(),
  email: authStore.user?.email || '',
}))

const passwordValidation = computed(() => {
  const p = changePasswordForm.value.newPassword || ''
  return {
    minLength: p.length >= 8,
    uppercase: /[A-Z]/.test(p),
    number: /[0-9]/.test(p),
    special: /[^A-Za-z0-9]/.test(p)
  }
})

const isPasswordValid = computed(() => {
  const v = passwordValidation.value
  return v.minLength && v.uppercase && v.number && v.special && 
         changePasswordForm.value.newPassword === changePasswordForm.value.confirmPassword
})

const formatDate = (dateStr) => {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('it-IT', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })
}

const getRegistrationDate = () => authStore.user?.createdAt || new Date()

const handleLogout = async () => {
  isLoggingOut.value = true
  try {
    await authStore.logout()
    toast.success(t('toast.success.logout'))
    if (configStore.isCountdownMode) {
      await router.push('/countdown')
    } else {
      await router.push('/')
    }
  } finally {
    isLoggingOut.value = false
  }
}

const startEdit = () => {
  editForm.value = {
    firstName: authStore.user?.firstName || '',
    lastName: authStore.user?.lastName || '',
    phone: authStore.user?.phone || '',
    isMarketingAllowed: authStore.user?.isMarketingAllowed || false
  }
  isEditMode.value = true
}

const cancelEdit = () => {
  isEditMode.value = false
}

const handleSaveProfile = async () => {
  try {
    await authStore.updateProfile(
      editForm.value.firstName, 
      editForm.value.lastName, 
      editForm.value.phone,
      editForm.value.isMarketingAllowed
    )
    toast.success(t('toast.success.profile_update'))
    isEditMode.value = false
  } catch (err) {
    const code = err?.response?.data?.code || 'SYSTEM_ERROR'
    toast.error(t('apiCodes.' + code))
  }
}

const openChangePasswordModal = () => {
  changePasswordForm.value = { currentPassword: '', newPassword: '', confirmPassword: '' }
  showChangePasswordModal.value = true
}

const closeChangePasswordModal = () => {
  showChangePasswordModal.value = false
}

const handleChangePassword = async () => {
  try {
    await authStore.changePassword(
      changePasswordForm.value.currentPassword,
      changePasswordForm.value.newPassword,
      changePasswordForm.value.confirmPassword
    )
    toast.success(t('toast.success.password_reset'))
    setTimeout(() => {
      closeChangePasswordModal()
      authStore.logout()
      if (configStore.isCountdownMode) {
        router.push('/login')
      } else {
        router.push('/login')
      }
    }, 2000)
  } catch (err) {
    const code = err?.response?.data?.code || 'SYSTEM_ERROR'
    changePasswordError.value = t('apiCodes.' + code)
  }
}
</script>

<style scoped>
.account-content {
  padding: 80px 0;
  background-color: #fafafa;
}

.auth-container {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 20px;
}

@media (max-width: 768px) {
  .auth-container {
    padding: 0 16px;
  }
}

@media (max-width: 480px) {
  .auth-container {
    padding: 0 12px;
  }
}

.account-sidebar {
  background: #fff;
  padding: 40px;
  border: 1px solid #f0f0f0;
  position: sticky;
  top: 130px;
}

@media (max-width: 991px) {
  .account-sidebar {
    position: static;
    padding: 24px 20px;
    margin-bottom: 20px;
  }
}

@media (max-width: 480px) {
  .account-sidebar {
    padding: 20px 16px;
  }
}

.account-nav .nav-link {
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  transition: all 0.3s ease;
  color: #999;
  border-bottom: 1px solid #f8f8f8;
  padding: 20px 0;
  position: relative;
  background: none;
  border-left: none;
  border-right: none;
  border-top: none;
  width: 100%;
}

.account-nav .nav-link:hover {
  color: #111;
  padding-left: 10px;
}

.account-nav .nav-link.active {
  color: #111;
  font-weight: 700;
  border-bottom-color: #111;
}

.account-nav .nav-link.active::before {
  content: '';
  position: absolute;
  left: -40px;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 20px;
  background-color: #111;
}

@media (max-width: 991px) {
  .account-nav .nav-link.active::before {
    display: none;
  }
}

.account-nav .nav-link.disabled {
  opacity: 0.4;
  cursor: not-allowed;
  pointer-events: none;
}

.account-main-content {
  background: #fff;
  padding: 50px;
  border: 1px solid #f0f0f0;
  min-height: 600px;
  box-shadow: 0 15px 40px rgba(0,0,0,0.02);
}

@media (max-width: 991px) {
  .account-main-content {
    padding: 30px 25px;
    min-height: 0;
  }
}

@media (max-width: 480px) {
  .account-main-content {
    padding: 22px 16px;
  }
}

.account-nav .nav-link.active::before {
  display: none;
}

@media (max-width: 991px) {
  .account-nav {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 8px;
  }
  .account-nav .nav-link {
    flex: 1 0 auto;
    padding: 10px 14px;
    font-size: 10px;
    border: 1px solid #eee;
  }
  .account-nav .nav-link.active {
    border-color: #111;
  }
}

.info-row {
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f8f8f8;
  flex-wrap: wrap;
  gap: 8px;
}

@media (max-width: 480px) {
  .info-row-compact .info-label,
  .info-label {
    flex: 0 0 100%;
    font-size: 9px;
  }
  .info-value {
    font-size: 12px;
  }
}

.modal-content {
  max-height: 90vh;
  max-height: 90dvh;
  overflow-y: auto;
}

@media (max-width: 576px) {
  .modal-content {
    width: 95%;
    margin: 12px;
  }
  .modal-form {
    padding: 18px !important;
  }
  .pending-orders-alert {
    flex-direction: column;
    align-items: flex-start !important;
    gap: 12px;
  }
}

@media (max-width: 380px) {
  .auth-container {
    padding: 0 10px;
  }
  .account-main-content {
    padding: 18px 12px;
  }
}

.pending-orders-alert {
  background: #fff9e6;
  border: 1px solid #ffeeba;
  border-left: 4px solid #ffc107;
  border-radius: 0;
}

.pending-orders-alert .alert-icon {
  color: #856404;
}

.info-row {
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f8f8f8;
}

.info-row-compact {
  padding: 6px 0;
}

.info-row-compact .info-label {
  flex: 0 0 130px;
}

.info-label {
  flex: 0 0 130px;
  font-weight: 500;
  color: #aaa;
  font-size: 8.5px;
  text-transform: uppercase;
  letter-spacing: 1.2px;
}

.info-value {
  flex: 1;
  color: #111;
  font-size: 12px;
  font-weight: 700;
}

.text-teal { color: var(--teal); }
.letter-spacing-1 { letter-spacing: 1.2px; }
.letter-spacing-2 { letter-spacing: 2px; }

.rounded-settings-box {
  border-radius: 4px;
  overflow: hidden;
}

.btn-premium-white {
  background-color: #fff;
  border: 1.5px solid #111;
  color: #111;
  transition: all 0.3s ease;
  font-size: 10px;
  padding: 8px 20px;
}

.btn-premium-white:hover {
  background-color: #111;
  color: #fff;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
}

.btn-outline-dark {
  border-radius: 0;
  border: 1px solid #111;
  background: transparent;
  color: #111;
  text-transform: uppercase;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 1.2px;
  padding: 6px 15px;
  transition: all 0.3s ease;
}

.btn-outline-dark:hover {
  background-color: #111;
  color: #fff;
}

.btn-dark {
  border-radius: 0;
  background-color: #111;
  border: 1px solid #111;
  color: #fff;
  text-transform: uppercase;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.5px;
  padding: 14px 30px;
  transition: all 0.3s ease;
}

.btn-dark:hover {
  background-color: #000;
  border-color: #000;
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
}

.form-label-premium {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: var(--teal);
  margin-bottom: 10px;
  display: block;
}

.form-control {
  border-radius: 0;
  border: 1px solid #e0e0e0;
  padding: 14px;
  font-size: 14px;
  transition: all 0.3s ease;
  width: 100%;
}

.form-control:focus {
  border-color: #111;
  box-shadow: none;
  background-color: #fff;
}

.password-wrapper {
  position: relative;
}

.password-toggle {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #aaa;
  cursor: pointer;
  padding: 0;
  display: flex;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.modal-content {
  background: #fff;
  width: 90%;
  max-width: 450px;
}

.modal-close {
  background: none;
  border: none;
  color: #111;
  cursor: pointer;
  padding: 10px;
  transition: opacity 0.2s;
}

.modal-close:hover { opacity: 0.6; }

/* Stili revisione visiva Dati Personali — coerenza premium con GuestOrderTracking */
.info-row-premium {
  display: flex;
  align-items: center;
  padding: 14px 0;
  border-bottom: 1px solid #f0f0f0;
  gap: 12px;
}

.info-row-premium .info-label {
  flex: 0 0 140px;
  font-size: 9px;
  font-weight: 700;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 1.2px;
}

.info-row-premium .info-value {
  flex: 1;
  font-size: 13px;
  font-weight: 600;
  color: #0d0d0b;
  letter-spacing: 0.3px;
}

/* Card dati profilo — stile tracking-card */
.profile-card {
  background: #fff;
  border: 1px solid #e4e4e4;
  padding: 0;
  margin-bottom: 12px;
}

.card-body {
  padding: 12px 24px;
}

.card-footer {
  padding: 8px 24px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
}

.footer-actions {
  gap: 8px;
  flex-wrap: wrap;
}

@media (max-width: 480px) {
  .footer-actions {
    justify-content: stretch !important;
  }
  .footer-actions > div {
    flex: 1;
  }
  .footer-actions button {
    width: 100%;
    padding: 10px 5px !important;
  }
}

.info-list {
  display: flex;
  flex-direction: column;
}

@media (max-width: 576px) {
  .privacy-consent-box {
    flex-direction: column;
    align-items: flex-start !important;
  }
  .privacy-consent-box button {
    width: 100%;
    margin-top: 10px;
  }
}

.field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  padding: 18px 0;
  border-bottom: 1px solid #e4e4e4;
}

@media (max-width: 576px) {
  .field-row {
    grid-template-columns: 1fr;
    gap: 16px;
    padding: 14px 0;
  }
}

.field-row:last-of-type {
  border-bottom: none;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.field-label {
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #999;
  font-weight: 700;
}

.field-value {
  font-size: 14px;
  font-weight: 600;
  color: #0d0d0b;
  letter-spacing: 0.3px;
}

.field-group.full {
  grid-column: 1 / -1;
}

.edit-form-wrap .premium-form {
  display: flex;
  flex-direction: column;
}

.form-group {
  margin-bottom: 0;
}

.form-label-premium {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: var(--teal);
  margin-bottom: 10px;
  display: block;
}

.form-control {
  border-radius: 0;
  border: 1px solid #e0e0e0;
  padding: 14px;
  font-size: 14px;
  transition: all 0.3s ease;
  width: 100%;
}

.form-control:focus {
  border-color: #111;
  box-shadow: none;
  background-color: #fff;
}

.card-actions {
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
  margin-top: 8px;
  border-top: 1px solid #f0f0f0;
}

/* Badge neutro per stati non critici (coerente con tracking) */
.badge-neutral {
  background: #f8f9fa;
  color: #37474f;
  border-color: #dee2e6;
}

.badge-positive {
  background: #eaf7ef;
  color: #1f7a45;
  border-color: #c3e6cb;
}

.badge-negative {
  background: #fff5f5;
  color: #880e4f;
  border-color: #fcc2c2;
}

.badge {
  display: inline-block;
  padding: 5px 9px;
  border-radius: 2px;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  border: 1px solid transparent;
  white-space: nowrap;
}

.border-bottom-dark {
  border-bottom: 1px solid #e4e4e4 !important;
}

.border-top-dark {
  border-top: 1px solid #e4e4e4 !important;
}

.btn-ghost-dark {
  background: transparent;
  border: 1px solid #0d0d0b;
  color: #0d0d0b;
  text-transform: uppercase;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 1.2px;
  padding: 6px 15px;
  transition: all 0.2s ease;
}

.btn-ghost-dark:hover {
  background: #0d0d0b;
  color: #fff;
}
</style>
